import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DealerNav from "./DealerNav";
import { getVehiclePath } from "../Utils/vehicleUrls";

export default function DealerLeads() {
  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem("dealerToken"), []);
  const API = process.env.REACT_APP_API_BASE;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("New");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // URL search params (filter lives here so it’s sharable/bookmarkable)
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";

  // Dealer SLA hours for labeling the dropdown (defaults to 24)
  const [slaHours, setSlaHours] = useState(24);

  useEffect(() => {
    if (!token) {
      navigate("/dealer/login", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Fetch SLA hours so “Overdue (Xh)” / “No-Touch (Xh)” match dealer settings
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API}/dealer/metrics/slas`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (r.status === 401) return navigate("/dealer/login", { replace: true });
        if (!r.ok) return;
        const j = await r.json();
        const h = Number(j?.hours ?? 24);
        if (!Number.isNaN(h) && h > 0) setSlaHours(h);
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, token]);

  // Helper to update the URL filter param (and reset page to 1)
  const setFilter = (val) => {
    const next = new URLSearchParams(searchParams);
    if (!val) next.delete("filter");
    else next.set("filter", val);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
  };

  const markRead = async (leadId) => {
    try {
      await fetch(`${API}/dealer/leads/${leadId}/LeadRead`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {}
  };

  const load = async () => {
    if (!token) return;
    try {
      setLoading(true);
      setErr("");
      const qs = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
      });
      if (status) qs.set("status", status);
      if (filter) qs.set("filter", filter); // ← send filter to API
      const res = await fetch(`${API}/dealer/leads?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.leads || []);
      setTotal(data.total || 0);
    } catch (e) {
      setErr("Could not load leads. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Load on status / page / filter changes
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page, filter]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [filter]);

  // periodic refresh (every 200s)
  useEffect(() => {
    const t = setInterval(() => load(), 200000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refresh on window focus / tab visibility
  useEffect(() => {
    const onFocus = () => load();
    const onVis = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, newStatus) => {
    if (!token) return;
    try {
      await fetch(`${API}/dealer/leads/${id}/status`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (status && status !== newStatus) {
        setRows((prev) => prev.filter((r) => r.id !== id));
        setTotal((t) => Math.max(0, t - 1));
      } else {
        load();
      }
    } catch {
      load();
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Dealer navigation */}
      <DealerNav />

      {/* Header / Controls */}
      <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4 mt-4">
        <h1 className="text-2xl font-semibold">Leads</h1>

        <div className="flex gap-3 sm:ml-auto">
          {/* Status */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Status</label>
            <select
              value={status}
              onChange={(e) => {
                setPage(1);
                setStatus(e.target.value);
              }}
              className="h-10 min-w-[180px] border border-gray-300 rounded-lg px-3 text-sm"
            >
              <option value="New">New</option>
              <option value="Contacted">Contacted</option>
              <option value="Qualified">Qualified</option>
              <option value="Closed">Closed</option>
              <option value="">All statuses</option>
            </select>
          </div>

          {/* SLA filter */}
          <div>
            <label className="block text-xs text-gray-600 mb-1">Filter</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="h-10 min-w-[180px] border border-gray-300 rounded-lg px-3 text-sm"
            >
              <option value="">All leads</option>
              <option value={`overdue${slaHours}`}>Overdue ({slaHours}h)</option>
              <option value={`notouch${slaHours}`}>No-Touch ({slaHours}h)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error alert */}
      {err && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="p-3 text-left">Buyer</th>
              <th className="p-3 text-left">Message</th>
              <th className="p-3">Mestimate</th>
              <th className="p-3">Car</th>
              <th className="p-3">Created</th>
              <th className="p-3">Updated</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-gray-500">
                  No leads found
                </td>
              </tr>
            )}

            {!loading &&
              rows.map((l) => (
                <tr key={l.id} className="border-t">
                  <td className="p-3 align-top">
                    <div className="font-medium">
                      {l.buyerName || l.buyerEmail || l.buyerPhone || "—"}
                    </div>
                    {l.buyerEmail && (
                      <div className="text-xs text-gray-500">{l.buyerEmail}</div>
                    )}
                    {l.buyerPhone && (
                      <div className="text-xs text-gray-500">{l.buyerPhone}</div>
                    )}
                  </td>

                  <td className="p-3 align-top max-w-[360px]">
                    <div className="line-clamp-3 text-gray-700" title={l.message || ""}>
                      {l.message || "—"}
                    </div>
                  </td>

                  <td className="p-3 align-top text-center">
                    {l.mestimate != null ? `$${Number(l.mestimate).toLocaleString()}` : "—"}
                  </td>

                  <td className="p-3 align-top text-center">
                    {l.carId ? (
                      <a
                        className="text-blue-600 underline"
                        href={getVehiclePath({ id: l.carId })}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>

                  <td className="p-3 align-top text-center">
                    {l.createdAt
                      ? new Date(l.createdAt).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>
                  <td className="p-3 align-top text-center">
                    {l.updatedAt
                      ? new Date(l.updatedAt).toLocaleString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })
                      : "—"}
                  </td>

                  <td className="p-3 align-top">
                    <div className="flex sm:justify-end gap-2">
                      <button
                        onClick={async () => {
                          await markRead(l.id);
                          navigate(`/dealer/leads/${l.id}`);
                        }}
                        className="h-10 px-4 text-sm rounded-lg border border-gray-300 bg-gray-200 text-gray-700 hover:bg-gray-300"
                      >
                        Open
                      </button>

                      <a
                        href={`/dealer/leads/${l.id}?tab=chat`}
                        className="relative h-10 px-4 text-sm rounded-lg border border-blue-600 bg-blue-50 text-blue-700 hover:bg-blue-100 flex items-center justify-center"
                      >
                        Chat
                        {(l.unreadForDealer ?? l.UnreadForDealer) && (
                          <span
                            className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-600"
                            title="New buyer message"
                          />
                        )}
                      </a>

                      <select
                        onChange={(e) => e.target.value && updateStatus(l.id, e.target.value)}
                        defaultValue=""
                        className="h-10 min-w-[100px] px-3 border border-gray-300 rounded-lg bg-white text-sm"
                        aria-label="Update lead status"
                      >
                        <option value="" disabled>
                          Set status…
                        </option>
                        <option>New</option>
                        <option>Contacted</option>
                        <option>Qualified</option>
                        <option>Closed</option>
                      </select>
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600">
          Page {page} of {totalPages} • {total} total
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white disabled:opacity-50"
          >
            Prev
          </button>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 bg-white disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}
